import {useCallback, useMemo} from 'react';

import styled from 'styled-components';
import {useDispatch} from 'react-redux';

import {useIsMinLargeMobile} from '~/shared/hooks/deviceInfo';
import {FlexColumn} from '~/shared/theme/FlexLayout';
import {media} from '~/shared/theme/media';
import {setCurrentModal} from '~/shared/store/storeModules/currentModal/currentModalActions';
import AddCreditCard from '~/shared/components/AddCreditCard';
import PaymentsList from '~/personalArea/components/Sections/Payments/PaymentsList';
import {trackEvent} from '~/shared/services/analytics';
import {useLocation} from '~/shared/router';
import {SimpleSpinnerLoader} from '~/shared/components/Loaders';
import {LoaderContainer} from '~/personalArea/components/Sections/Shared';
import useIsB2BUser from '~/common/hooks/useIsB2BUser';
import {convertPathnameToAnalyticsPageSource} from '~/shared/services/analytics/utils';
import {usePersonalAreaContext} from '~/personalArea/context';

import PaymentsEmptyState from './PaymentsEmptyState';

const Root = styled(FlexColumn)`
  height: 100%;
  padding: 24px 0;
  ${media.minLargeMobile`
    padding: 32px 0;
    height: auto;
  `}
  ${media.minLargeTablet`
    padding: 40px 0;
  `}
`;

const Payments = () => {
  const isMinLargeMobile = useIsMinLargeMobile();
  const dispatch = useDispatch();
  const location = useLocation();
  const isB2B = useIsB2BUser();

  const {payments, isLoaded, removePaymentMethodHandler, getUserPayments} = usePersonalAreaContext();

  const pageSource = useMemo(() => convertPathnameToAnalyticsPageSource(location.pathname), [location.pathname]);

  const onRemovePaymentClick = useCallback(({id, cardName, type}: {
    id: number;
    cardName: string;
    type: string;
  }) => {
    trackEvent('removePaymentMethod_click', {linkType: type === 'paypal' ? type : 'credit_card', pageSource});
    dispatch(setCurrentModal('removeCreditCardModal', {id, type, cardName, removePaymentMethodHandler}));
  }, [dispatch, removePaymentMethodHandler, pageSource]);

  const onAddPaymentMethodClick = useCallback(() => {
    trackEvent('addPaymentMethod_click', {pageSource});
    dispatch(setCurrentModal('addPaymentMethodModal', {
      AddCreditCardComponent: AddCreditCard,
      pageSource,
      onSuccess: () => {
        getUserPayments();
      },
    }));
  }, [dispatch, getUserPayments, pageSource]);

  if (!isLoaded) {
    return (
      <LoaderContainer>
        <SimpleSpinnerLoader variant="pieBig" />
      </LoaderContainer>
    );
  }

  return (
    <Root>
      {!payments?.length && <PaymentsEmptyState onAddPaymentClick={onAddPaymentMethodClick} isMinLargeMobile={isMinLargeMobile} isB2B={isB2B} />}
      {!!payments?.length && (
        <PaymentsList
          payments={payments}
          onRemovePaymentClick={onRemovePaymentClick}
          onAddPaymentMethodClick={onAddPaymentMethodClick}
          isMinLargeMobile={isMinLargeMobile}
        />
      )}
    </Root>
  );
};

export default Payments;
